<script>
import useSharedData from "@/use/SharedData.js";
import useSharedMethod from "@/use/SharedMethod.js";
import useTools from "@/use/Tools.js";
import {useSecurityStore} from "@/stores/Security.js";
import {ref, watch} from "vue";
import {useContratTemplateStore} from "@/stores/ContratTemplate.js";
import SimpleContratTemplateFilter from "@/views/ContratTemplate/Components/SimpleContratTemplateFilter.vue";
import axios from "axios";

export default {
    components: {SimpleContratTemplateFilter},
    setup() {
        const {
            loading,
            total,
            itemsPerPage,
        } = useSharedData();

        const {getBack, transformFilterValues} = useSharedMethod();

        const {$router, $i18n} = useTools();


        const contratTemplateStore = useContratTemplateStore();
        const securityStore = useSecurityStore();

        const filter = ref({archive: false});

        const headers = [
            {
                text: "...",
                align: "start",
                sortable: false,
                value: "action",
                width: "0%"
            },
            {
                text: $i18n.t("common.code"),
                align: "start",
                sortable: true,
                value: "code",
            },
            {
                text: $i18n.t("common.label"),
                align: "start",
                sortable: true,
                value: "name",
            },
            {
                text: $i18n.t("contrat.duration"),
                align: "start",
                sortable: true,
                value: "duration",
            },
            {
                text: $i18n.t("contrat.trial_period"),
                align: "start",
                sortable: true,
                value: "trialPeriodDuration",
            },
            {
                text: $i18n.t("common.state"),
                align: "start",
                sortable: true,
                value: "archive",
            },
        ];

        const actions = [
            {
                title: $i18n.t("common.edit"),
                icon: 'mdi-pencil-box-multiple',
                color: 'orange',
                access: () => securityStore.authorities['UPDATE_CONTRAT_TEMPLATE'],
                disabled: () => false,
                func: (item) => {
                    $router.push({name: 'contrat_templates.edit', params: {id: item.id}})
                },
            },
            {
                title: $i18n.t("common.delete"),
                icon: 'mdi-trash-can',
                color: 'red',
                access: () => securityStore.authorities['DELETE_CONTRAT_TEMPLATE'],
                disabled: () => false,
                func: (item) => {
                    $router.push({name: 'contrat_templates.delete', params: {id: item.id}})
                },
            },
            {
                title: $i18n.t('common.archive'),
                icon: 'mdi-archive',
                color: 'purple',
                access: (item) => securityStore.authorities['UPDATE_CONTRAT_TEMPLATE'] && !item.archive,
                disabled: () => false,
                func: (item) => {
                    $router.push({name: 'contrat_templates.archive', params: {id: item.id}})
                },
            },
            {
                title: $i18n.t('common.unarchive'),
                icon: 'mdi-archive',
                color: 'purple',
                access: (item) => securityStore.authorities['UPDATE_CONTRAT_TEMPLATE'] && item.archive,
                disabled: () => false,
                func: (item) => {
                    $router.push({name: 'contrat_templates.unarchive', params: {id: item.id}})
                },
            },
            {
                title: $i18n.t("module.edms"),
                icon: 'mdi-folder',
                color: 'amber',
                access: () => true,
                disabled: () => false,
                func: (item) => {
                    $router.push({name: `contrat_templates.documents`, params: {id: item.id}})
                },
            },
            {
                title: $i18n.t("module.template_application"),
                icon: 'mdi-text-box-multiple',
                color: 'teal',
                access: () => true,
                disabled: () => false,
                func: (item) => {
                    $router.push({name: 'contrat_templates.template_applications', params: {id: item.id}})
                },
            },
        ];

        const searchParams = ref({
            sortBy: "name",
            descending: false,
            page: 1,
            itemsPerPage: itemsPerPage,
        });

        function handleSearch(params) {
            loading.value = true;
            contratTemplateStore.setContratTemplates([]);
            total.value = 0;

            loading.value = true;
            filter.value.page = params.page - 1;
            filter.value.rowsPerPage = params.itemsPerPage;
            filter.value.sortBy = params.sortBy;
            filter.value.descending = params.descending;

            axios.get('/contratTemplates', {params: transformFilterValues(filter.value)}).then((response) => {
                contratTemplateStore.setContratTemplates(response.data.elements);
                total.value = response.data.total;
            }).finally(() => {
                loading.value = false;
            });
        }

        function findAll() {
            handleSearch(searchParams.value)
        }

        watch(() => filter.value, () => {
            searchParams.page = 1;
            findAll();
        }, {deep: true, immediate: true});


        watch(searchParams, (newParams) => {
            handleSearch(newParams);
        }, {deep: true});


        function view(item) {
            $router.push({name: "contrat_templates.view", params: {id: item.id}});
        }

        return {
            contratTemplateStore,
            securityStore,
            headers,
            loading,
            total,
            itemsPerPage,
            filter,
            searchParams,
            actions,
            findAll,
            getBack,
            view,
        };
    },
}
</script>

<template>
    <div>
        <w-app
            :title="$t('module.contrat_template')"
            show-total
            :total="total"
        >
            <template v-slot:rightToolbar>
                <w-btn-reload :loading="loading" @press="findAll"/>
                <w-btn-add :show="securityStore.authorities.SAVE_CONTRAT_TEMPLATE" @press="$router.push({name: 'contrat_templates.add'})"/>
            </template>

            <template v-slot:content>
                <v-container fluid>
                    <SimpleContratTemplateFilter :filter="filter"></SimpleContratTemplateFilter>
                </v-container>
                <w-server-data-table
                    v-model="searchParams"
                    :actions="actions"
                    :headers="headers"
                    :items="contratTemplateStore.contratTemplates"
                    :loading="loading"
                    :total="total"
                    root-path="contrats"
                    module="CONTRAT_TEMPLATE"
                    @rowDbClick="view"
                >
                    <template v-slot:[`item.code`]="{ item }">
                        <w-chip :text="item.code"/>
                    </template>
                    <template v-slot:[`item.name`]="{ item }">
                        <w-inline-text bold :text="item.name"/>
                    </template>
                    <template v-slot:[`item.duration`]="{ item }">
                        <w-chip color="teal" :text="item.determinedTime ? `${item.duration} ${$t(`common.${item.periodicity}`)}` : $t('contrat.cdi')"/>
                    </template>
                    <template v-slot:[`item.archive`]="{ item }">
                        <w-inline-text :text="item.archive ? $t('common.archive') : $t('common.in_progress')"/>
                    </template>
                    <template v-slot:[`item.trialPeriodDuration`]="{ item }">
                        <w-inline-text :text="item.trialPeriod ? `${item.trialPeriodDuration} ${$t(`common.${item.trialPeriodPeriodicity}`)}` : $t('contrat.without_trial_period')"/>
                        <strong
                            v-if="item.renewedTrialPeriod && item.trialPeriod"
                        >
                            , {{ $t('contrat.renewed_trial_period') }}
                        </strong>
                    </template>
                </w-server-data-table>
            </template>
        </w-app>
        <router-view/>
    </div>
</template>